import { useCallback, useMemo } from 'react'

import { isInternalRoleName } from 'constants/roles'
import { useAuth } from './useAuth'
import { useWorkOSAuth } from './useWorkOSAuth'
import { ruleForInternalPath, satisfiesInternalRule } from 'routes'
import { useVeraSpaceStore } from 'stores'

/*
  The role-name list itself lives in `constants/roles` (VRG-410) — a leaf module — so
  `useWorkOSAuth` can use it without importing this hook, which imports it. Re-exported
  here because this file is where every existing consumer looks for it, and the predicate
  was already being re-derived inline in five places by the time VRG-395 needed a sixth.
*/
export { isInternalRoleName }

/**
 * Is the signed-in user Vera staff operating AS Vera staff?
 *
 * Two clauses, and the second is the one that is easy to forget. VERA-876 lets a
 * staffer enter a company's *space*, where they operate as that brand's user: their
 * permissions are replaced by the brand's `vera_support` grant, the internal nav is
 * hidden and `InternalRoute` bounces them out. So "has an internal role" is NOT the
 * same question as "is acting internally", and every surface that switches on staff-ness
 * — the company settings menu, the sidebar's Settings gear — has to ask the second one.
 * A staffer in a brand's space needs that brand's settings, which is exactly what they
 * entered the space to do.
 *
 * Distinct from `useCanAccess({ role: 'platform_admin' })`, which asks "may this role do
 * X". That question is answered correctly by the permission system; this one is not,
 * because an internal role legitimately *holds* the company permissions while the user
 * behind it has no company for them to apply to (VRG-395).
 *
 * Safe to call before auth settles: every consumer renders under `ProtectedRoute`, which
 * holds a loader until `isLoading` clears, so `user` is resolved by the time this runs.
 */
export const useIsInternalUser = (): boolean => {
  const { user } = useWorkOSAuth()
  const inSpace = useVeraSpaceStore((s) => !!s.companyId)
  return !inSpace && isInternalRoleName(user?.role?.name)
}

/**
 * Is this staffer allowed to OPEN a given `/internal/*` page?
 *
 * Two questions, and both have to be asked (VRG-410). "Is this Vera staff" admits
 * someone to the console; it does not say which pages inside it they can use. Admitting
 * the four internal roles without the second question would hand `vera_fulfillment` a
 * Subscriptions page that 403s on every call — the same defect the guard already had,
 * pointed the other way.
 *
 * ⚠️ THE ROLE NAME COMES FROM `useWorkOSAuth`, NOT `useAuth().profile`. `useAuth`'s
 * `mapWorkOSUserToProfile` maps role names through a four-entry table and falls back to
 * `'company_team_member'` for anything unlisted — so a `vera_fulfillment` user's
 * `profile.role.name` reads as a company member. Permissions pass through that mapping
 * verbatim, which is why the permission half below can safely use the profile.
 *
 * Returns a stable predicate rather than a boolean because the callers ask about many
 * paths (the sidebar asks once per row) and hooks cannot be called in a loop.
 */
export const useInternalRouteAccess = () => {
  const { user } = useWorkOSAuth()
  const { profile } = useAuth()
  const inSpace = useVeraSpaceStore((s) => !!s.companyId)

  const roleName = user?.role?.name
  const isInternal = !inSpace && isInternalRoleName(roleName)

  const granted = useMemo(
    () => new Set((profile?.role?.permissions ?? []).map((permission) => permission.name as string)),
    [profile],
  )

  const canReachInternalPath = useCallback(
    (pathname: string): boolean =>
      isInternal && satisfiesInternalRule(ruleForInternalPath(pathname), roleName, granted),
    [isInternal, roleName, granted],
  )

  return { isInternal, canReachInternalPath }
}
